import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  ExpirationType,
  ExpirationTypeDetails,
  Product,
  ProductType,
  ProductsService,
  QuickbooksCategory,
  UpdateProductRequest,
} from 'src/app/services/products.service';

@Component({
  selector: 'app-edit-product',
  templateUrl: './edit-product.component.html',
  styleUrls: ['./edit-product.component.scss'],
})
export class EditProductComponent {
  @ViewChild('changeDialog') changeDialog!: ElementRef<HTMLDialogElement>;

  loadingProduct = false;
  productTypes = this.productsService.productTypes;
  originalProduct?: Product;
  errorFetchingProduct = '';

  active!: boolean;
  type!: ProductType;
  isService!: boolean;
  code!: string;
  varianceCode!: string;
  standalone!: boolean;
  isDefault!: boolean;
  requiresParent!: boolean;
  title!: string;
  description!: string;
  expirationType!: ExpirationType;
  quickbooksCategoryId!: number;

  quickbooksCategories?: QuickbooksCategory[];
  fetchingQuickbooksCategories = false;
  errorFetchingQuickbooksCategories = '';

  savingChanges = false;
  productUpdated = false;
  errorUpdatingProduct = '';

  expirationTypes: ExpirationType[] = [
    'desktop',
    'annual',
    'force_extension',
    'physical',
    'transactional',
  ];

  ExpirationTypeDetails = ExpirationTypeDetails;

  constructor(
    public location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private productsService: ProductsService
  ) {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      console.error(new Error('No product ID found in route'));
      this.errorFetchingProduct = 'No product ID found';
      return;
    }
    this.getProduct(id);
    this.fetchQuickbooksCategories();
  }

  getProduct(id: string) {
    this.loadingProduct = true;
    this.productsService.getProduct(id).subscribe({
      next: (res) => {
        this.loadingProduct = false;
        console.log('res: ', res);
        if (res.product) {
          this.originalProduct = res.product;
          this.initializeData(this.originalProduct);
        } else {
          console.error(new Error('No product returned from server'));
          this.errorFetchingProduct = 'No product returned from server';
        }
      },
      error: (err) => {
        this.loadingProduct = false;
        console.error(err);
        this.errorFetchingProduct = err;
      },
    });
  }

  fetchQuickbooksCategories() {
    this.fetchingQuickbooksCategories = true;
    this.errorFetchingQuickbooksCategories = '';
    this.productsService.getQuickbooksCategories().subscribe({
      next: (response) => {
        this.fetchingQuickbooksCategories = false;
        this.quickbooksCategories = response;
      },
      error: (error) => {
        this.fetchingQuickbooksCategories = false;
        console.error(error);
        this.errorFetchingQuickbooksCategories = error;
      },
    });
  }

  initializeData(product: Product) {
    this.active = product.active;
    this.type = product.type;
    this.isService = product.is_service;
    this.code = product.code;
    this.varianceCode = product.variance_code;
    this.standalone = product.standalone;
    this.isDefault = product.is_default;
    this.requiresParent = product.requires_parent;
    this.title = product.title;
    this.description = product.description;
    this.expirationType = product.expiration_type;
    this.quickbooksCategoryId = product.quickbooks_category_id;
  }

  handleQuickbooksCategoryChange(ev: Event) {
    if (!ev.target) return;
    const target = ev.target as HTMLSelectElement;
    if (!target.value) return;

    // convert to number
    const id = Number(target.value);
    // verify the category is valid
    if (!this.quickbooksCategories?.find((c) => c.id === id)) return;

    this.quickbooksCategoryId = id;
  }

  getQuickbooksCategoryName(id?: number) {
    return this.quickbooksCategories?.find((c) => c.id === id)?.class_name;
  }

  saveChanges() {
    if (!this.originalProduct) return;

    this.closeChangeDialog();
    this.savingChanges = true;
    this.productUpdated = false;
    this.errorUpdatingProduct = '';
    const updates = this.productUpdates();

    this.productsService
      .updateProduct(this.originalProduct.id, updates)
      .subscribe({
        next: (response) => {
          console.log('response: ', response);
          this.productUpdated = true;
          this.originalProduct = response.product;
        },
        error: (error) => {
          console.error(error);
          this.errorUpdatingProduct = error.message;
        },
        complete: () => {
          this.savingChanges = false;
        },
      });
  }

  get hasChanges() {
    if (!this.originalProduct) return false;
    return (
      this.active !== this.originalProduct.active ||
      this.type !== this.originalProduct.type ||
      this.isService !== this.originalProduct.is_service ||
      this.code !== this.originalProduct.code ||
      this.standalone !== this.originalProduct.standalone ||
      this.isDefault !== this.originalProduct.is_default ||
      this.title !== this.originalProduct.title ||
      this.description !== this.originalProduct.description ||
      this.requiresParent !== this.originalProduct.requires_parent ||
      this.expirationType !== this.originalProduct.expiration_type ||
      this.quickbooksCategoryId !== this.originalProduct.quickbooks_category_id
    );
  }

  productUpdates() {
    if (!this.originalProduct) return {};
    const updates: UpdateProductRequest = {};
    if (this.active !== this.originalProduct.active) {
      updates.active = this.active;
    }
    if (this.type !== this.originalProduct.type) {
      updates.type = this.type;
    }
    if (this.isService !== this.originalProduct.is_service) {
      updates.is_service = this.isService;
    }
    if (this.code !== this.originalProduct.code) {
      updates.code = this.code;
    }
    if (this.standalone !== this.originalProduct.standalone) {
      updates.standalone = this.standalone;
    }
    if (this.isDefault !== this.originalProduct.is_default) {
      updates.is_default = this.isDefault;
    }
    if (this.title !== this.originalProduct.title) {
      updates.title = this.title;
    }
    if (this.description !== this.originalProduct.description) {
      updates.description = this.description;
    }
    if (this.requiresParent !== this.originalProduct.requires_parent) {
      updates.requires_parent = this.requiresParent;
    }
    if (this.expirationType !== this.originalProduct.expiration_type) {
      updates.expiration_type = this.expirationType;
    }
    if (
      this.quickbooksCategoryId !== this.originalProduct.quickbooks_category_id
    ) {
      updates.quickbooks_category_id = this.quickbooksCategoryId;
    }
    return updates;
  }

  closeChangeDialog() {
    this.changeDialog.nativeElement.close();
  }

  navigateToProduct() {
    if (!this.originalProduct) return;
    this.router.navigate(['/admin/product', this.originalProduct.id]);
  }
}
